import { ROUTES } from '@/constants/app';
import { useSetSecurityMethod } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/auth.store';
import { format } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import {
  Bell,
  Calendar,
  Check,
  Globe,
  Loader2,
  Mail,
  Settings,
  Shield,
  Smartphone,
  User as UserIcon,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TwoFactorSetupModal } from '../components/TwoFactorSetupModal';

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const [isTwoFactorModalOpen, setIsTwoFactorModalOpen] = useState(false);
  const setSecurityMethod = useSetSecurityMethod();

  const isTotp = user?.securityMethod === 'totp';
  const isOtp = user?.securityMethod === 'otp';
  const hasAnyMethod = isTotp || isOtp;

  if (!user) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="space-y-3 text-center">
          <div className="border-primary-600 mx-auto h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"></div>
          <p className="text-neutral-600">
            {t('profile.loading', 'Chargement du profil...')}
          </p>
        </div>
      </div>
    );
  }

  const fullName = `${user.firstName} ${user.lastName}`;
  const memberSince = format(new Date(user.createdAt), 'MMMM yyyy', {
    locale: i18n.language === 'fr' ? fr : enUS,
  });

  const lastLogin = user.lastLoginAt
    ? format(new Date(user.lastLoginAt), 'dd/MM/yyyy HH:mm', {
      locale: i18n.language === 'fr' ? fr : enUS,
    })
    : t('profile.never_logged_in', 'Jamais connecté');

  return (
    <div className="mx-auto max-w-6xl space-y-8 p-6">
      {/* Header */}
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-bold text-neutral-900">
          {t('profile.title', 'Mon Profil')}
        </h1>
        <p className="mx-auto max-w-md text-neutral-600">
          {t(
            'profile.description',
            'Gérez vos informations personnelles et vos préférences'
          )}
        </p>
      </div>

      {/* Profile Header Card */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-8">
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
          {/* Avatar */}
          <div className="relative">
            <div className="bg-primary-50 border-primary-100 flex h-24 w-24 items-center justify-center rounded-2xl border-2">
              <span className="text-primary-700 text-2xl font-bold">
                {user.firstName.charAt(0)}
                {user.lastName.charAt(0)}
              </span>
            </div>
            {/* <div className="absolute -right-2 -bottom-2 flex h-8 w-8 items-center justify-center rounded-xl border-2 border-neutral-200 bg-white">
              <Edit3 className="h-4 w-4 text-neutral-600" />
            </div> */}
          </div>

          {/* User Info */}
          <div className="flex-1 space-y-3 text-center sm:text-left">
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">
                {fullName}
              </h2>
              <p className="text-neutral-600">{user.email}</p>
            </div>

            <div className="flex flex-wrap justify-center gap-3 sm:justify-start">
              {/* Status Badge */}
              <div
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-medium ${user.isActive
                    ? 'bg-success-50 text-success-700 border-success-200 border'
                    : 'bg-error-50 text-error-700 border-red-200 border'
                  }`}
              >
                {user.isActive ? (
                  <>
                    <div className="bg-success-600 h-2 w-2 rounded-full"></div>
                    {t('profile.active', 'Actif')}
                  </>
                ) : (
                  <>
                    <div className="bg-error-600 h-2 w-2 rounded-full"></div>
                    {t('profile.inactive', 'Inactif')}
                  </>
                )}
              </div>

              {/* Member Since */}
              <div className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm font-medium text-neutral-700">
                <Calendar className="h-3 w-3" />
                {t('profile.member_since', 'Membre depuis {{date}}', {
                  date: memberSince,
                })}
              </div>
            </div>
          </div>

          {/* Action Button */}
          {/* <button className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 font-medium text-white transition-colors">
            <Edit3 className="h-4 w-4" />
            {t('profile.edit', 'Modifier')}
          </button> */}
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Personal Information */}
        <div className="space-y-6">
          <h3 className="flex items-center gap-2 text-xl font-bold text-neutral-900">
            <UserIcon className="text-primary-600 h-5 w-5" />
            {t('profile.personal_info', 'Informations personnelles')}
          </h3>

          <div className="space-y-4">
            {/* Email */}
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                    <Mail className="h-5 w-5 text-neutral-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-900">
                      Email
                    </p>
                    <p className="text-neutral-600">{user.email}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Language */}
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                    <Globe className="h-5 w-5 text-neutral-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-900">
                      {t('profile.language', 'Langue préférée')}
                    </p>
                    <p className="text-neutral-600">
                      {user.preferredLanguage === 'fr' ? 'Français' : 'English'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Last Login */}
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                    <Calendar className="h-5 w-5 text-neutral-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-900">
                      {t('profile.last_login', 'Dernière connexion')}
                    </p>
                    <p className="text-neutral-600">{lastLogin}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security & Preferences */}
        <div className="space-y-6">
          {/* Security */}
          <div>
            <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-neutral-900">
              <Shield className="text-primary-600 h-5 w-5" />
              {t('profile.security_title', 'Sécurité')}
            </h3>

            <div className="space-y-3">
              {/* TOTP - Application d'authentification */}
              <div
                className={`rounded-xl border bg-white p-4 transition-colors ${
                  isTotp ? 'border-primary-300 ring-1 ring-primary-100' : 'border-neutral-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                        isTotp ? 'bg-primary-50' : 'bg-neutral-50'
                      }`}
                    >
                      <Smartphone className={`h-5 w-5 ${isTotp ? 'text-primary-600' : 'text-neutral-600'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-900">
                        {t('profile.totp_title', "Application d'authentification")}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {t(
                          'profile.totp_desc',
                          'Google Authenticator, Authy, Microsoft Authenticator'
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isTotp && (
                      <div className="flex items-center gap-1.5 rounded-lg bg-success-50 px-3 py-1.5 text-sm font-medium text-success-700">
                        <Check className="h-3 w-3" />
                        {t('common.active', 'Actif')}
                      </div>
                    )}
                    {isTotp ? (
                      <button
                        onClick={() => setIsTwoFactorModalOpen(true)}
                        className="rounded-lg border border-error-300 bg-error-50 px-3 py-1.5 text-sm font-medium text-error-700 transition-colors hover:bg-error-100"
                      >
                        {t('common.disable', 'Désactiver')}
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsTwoFactorModalOpen(true)}
                        disabled={setSecurityMethod.isPending}
                        className="rounded-lg border border-primary-300 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-50"
                      >
                        {t('common.enable', 'Activer')}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* OTP - Code par email */}
              <div
                className={`rounded-xl border bg-white p-4 transition-colors ${
                  isOtp ? 'border-primary-300 ring-1 ring-primary-100' : 'border-neutral-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                        isOtp ? 'bg-primary-50' : 'bg-neutral-50'
                      }`}
                    >
                      <Mail className={`h-5 w-5 ${isOtp ? 'text-primary-600' : 'text-neutral-600'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-900">
                        {t('profile.otp_title', 'Code par email')}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {t(
                          'profile.otp_desc',
                          'Recevez un code de vérification par email à chaque connexion'
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isOtp && (
                      <div className="flex items-center gap-1.5 rounded-lg bg-success-50 px-3 py-1.5 text-sm font-medium text-success-700">
                        <Check className="h-3 w-3" />
                        {t('common.active', 'Actif')}
                      </div>
                    )}
                    {isOtp ? (
                      <button
                        onClick={() => setSecurityMethod.mutate(null)}
                        disabled={setSecurityMethod.isPending}
                        className="rounded-lg border border-error-300 bg-error-50 px-3 py-1.5 text-sm font-medium text-error-700 transition-colors hover:bg-error-100 disabled:opacity-50"
                      >
                        {setSecurityMethod.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          t('common.disable', 'Désactiver')
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => setSecurityMethod.mutate('otp')}
                        disabled={setSecurityMethod.isPending}
                        className="rounded-lg border border-primary-300 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-50"
                      >
                        {setSecurityMethod.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          t('common.enable', 'Activer')
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Info Banner */}
              {hasAnyMethod && (
                <div className="rounded-lg border border-success-200 bg-success-50 p-3">
                  <p className="text-xs text-success-700">
                    {isTotp
                      ? t(
                          'profile.totp_active_info',
                          "Votre compte est protégé par une application d'authentification. Vous devrez entrer un code généré par l'application lors de chaque connexion."
                        )
                      : t(
                          'profile.otp_active_info',
                          'Votre compte est protégé par vérification email. Un code vous sera envoyé par email lors de chaque connexion.'
                        )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Notifications */}
          <div>
            <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-neutral-900">
              <Bell className="text-primary-600 h-5 w-5" />
              {t('profile.notifications_title', 'Notifications')}
            </h3>

            <div className="space-y-3">
              {/* Email Notifications */}
              <div className="rounded-xl border border-neutral-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                      <Mail className="h-5 w-5 text-neutral-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-900">
                        {t(
                          'profile.email_notifications',
                          'Notifications par email'
                        )}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {t(
                          'profile.email_notifications_desc',
                          'Recevoir des notifications par email'
                        )}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${user.emailNotifications
                        ? 'bg-success-50 text-success-700'
                        : 'bg-neutral-100 text-neutral-600'
                      }`}
                  >
                    {user.emailNotifications ? (
                      <>
                        <Check className="h-3 w-3" />
                        {t('common.enabled', 'Activé')}
                      </>
                    ) : (
                      <>
                        <X className="h-3 w-3" />
                        {t('common.disabled', 'Désactivé')}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Push Notifications */}
              <div className="rounded-xl border border-neutral-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                      <Bell className="h-5 w-5 text-neutral-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-900">
                        {t('profile.push_notifications', 'Notifications push')}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {t(
                          'profile.push_notifications_desc',
                          'Recevoir des notifications push'
                        )}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${user.pushNotifications
                        ? 'bg-success-50 text-success-700'
                        : 'bg-neutral-100 text-neutral-600'
                      }`}
                  >
                    {user.pushNotifications ? (
                      <>
                        <Check className="h-3 w-3" />
                        {t('common.enabled', 'Activé')}
                      </>
                    ) : (
                      <>
                        <X className="h-3 w-3" />
                        {t('common.disabled', 'Désactivé')}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Link
              to={ROUTES.SETTINGS}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-neutral-100 px-4 py-2.5 font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
            >
              <Settings className="h-4 w-4" />
              {t('profile.edit_notifications', 'Modifier les préférences')}
            </Link>
          </div>

          {/* Subscription */}
          {user.subscription && (
            <div>
              <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-neutral-900">
                <div className="bg-primary-600 h-5 w-5 rounded"></div>
                {t('profile.subscription_title', 'Abonnement')}
              </h3>

              <div className="rounded-xl border border-neutral-200 bg-white p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-900">
                      {t('profile.subscription_status', 'Statut')}
                    </p>
                    <p className="text-lg font-semibold text-neutral-900 capitalize">
                      {user.subscription.status}
                    </p>
                  </div>
                  <div
                    className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium ${user.subscription.status === 'active'
                        ? 'bg-success-50 text-success-700 border-success-200 border'
                        : 'bg-warning-50 text-warning-700 border-warning-200 border'
                      }`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full ${user.subscription.status === 'active'
                          ? 'bg-success-600'
                          : 'bg-warning-600'
                        }`}
                    ></div>
                    {user.subscription.status === 'active'
                      ? t('subscription.active', 'Actif')
                      : user.subscription.status}
                  </div>
                </div>

                {user.subscription.status === 'active' && (
                  <div className="border-t border-neutral-100 pt-4">
                    <p className="text-sm font-medium text-neutral-900">
                      {t('profile.subscription_end', 'Fin de période')}
                    </p>
                    <p className="text-neutral-600">
                      {format(
                        new Date(user.subscription.currentPeriodEnd),
                        'dd MMMM yyyy',
                        { locale: i18n.language === 'fr' ? fr : enUS }
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Two-Factor Setup/Disable Modal */}
      <TwoFactorSetupModal
        isOpen={isTwoFactorModalOpen}
        onClose={() => setIsTwoFactorModalOpen(false)}
        enabled={user.twoFactorEnabled}
        onSuccess={() => {
          // Reload profile to get updated 2FA status
          window.location.reload();
        }}
      />
    </div>
  );
}
